import { useParams, Navigate, Link } from "react-router-dom";
import { SITE_URL } from "@/config/site";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { deepWithStats } from "@/data/modelStats";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Sparkles,
  ClipboardList,
  Users,
  GraduationCap,
  Megaphone,
  Briefcase,
  Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

const ICONS = {
  ClipboardList,
  Users,
  GraduationCap,
  Megaphone,
  Briefcase,
  Rocket,
};

// Public per-profession kit landing page (/kits/:slug, /:lng/kits/:slug).
// i18n-driven (kits.* namespace, 15 languages), slugs invariant across locales.
// Describes the included templates + CTA — prompt bodies stay gated in the
// dashboard. Prerendered by scripts/prerender-seo.js + sitemap.
const KitPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const localePrefix = useLocalePrefix();
  const rawKits = deepWithStats(t("kits.list", { returnObjects: true }));
  const kits = Array.isArray(rawKits) ? rawKits : [];
  const kit = kits.find((k) => k.slug === slug);
  if (!kit) return <Navigate to={`${localePrefix}/kits`} replace />;

  const Icon = ICONS[kit.icon] || Sparkles;
  const related = kits.filter((k) => k.slug !== kit.slug).slice(0, 3);
  const title = `${kit.title} — ${t("kits.detailTitleSuffix")}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t("kits.crumbHome"),
            item: `${SITE_URL}${localePrefix}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t("kits.crumbKits"),
            item: `${SITE_URL}${localePrefix}/kits`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: kit.title,
            item: `${SITE_URL}${localePrefix}/kits/${kit.slug}`,
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: t("kits.faqWhat", { profession: kit.profession }),
            acceptedAnswer: { "@type": "Answer", text: kit.description },
          },
          {
            "@type": "Question",
            name: t("kits.faqTemplates"),
            acceptedAnswer: {
              "@type": "Answer",
              text: t("kits.faqTemplatesAnswer", {
                count: kit.templates.length,
                list: kit.templates.join(", "),
              }),
            },
          },
        ],
      },
    ],
  };

  return (
    <PageLayout>
      <SEO
        title={title}
        description={kit.description}
        canonical={`${localePrefix}/kits/${kit.slug}`}
        jsonLd={jsonLd}
      />
      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <nav
            className="flex items-center gap-1 text-sm text-slate-400 mb-8"
            aria-label="Fil d'Ariane"
          >
            <Link to={`${localePrefix}/`} className="hover:text-primary">
              {t("kits.crumbHome")}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`${localePrefix}/kits`} className="hover:text-primary">
              {t("kits.crumbKits")}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{kit.title}</span>
          </nav>

          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            {kit.title}
          </h1>
          <p className="text-xl text-slate-300 mb-8">{kit.tagline}</p>

          <p className="text-slate-300 leading-relaxed mb-10">
            {kit.description}
          </p>

          <div className="glass rounded-2xl p-6 mb-10">
            <h2 className="font-heading text-lg font-semibold text-white mb-4">
              {t("kits.includedCountTitle", { count: kit.templates.length })}
            </h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {kit.templates.map((tpl) => (
                <li
                  key={tpl}
                  className="flex items-start gap-2 text-slate-300 text-sm"
                >
                  <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>{tpl}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="glass rounded-2xl p-6 mb-12">
            <h2 className="font-heading text-lg font-semibold text-white mb-2">
              {t("kits.includedTitle")}
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              {t("kits.includedNote")}
            </p>
            <Link to="/register">
              <Button className="btn-glow bg-primary-strong hover:bg-primary-strong/90">
                {t("kits.tryBtn")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {related.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                {t("kits.relatedTitle")}
              </h2>
              <div className="flex flex-wrap gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`${localePrefix}/kits/${r.slug}`}
                    className="glass rounded-lg px-4 py-2 text-sm text-slate-300 hover:text-primary hover:border-primary/30 transition-all"
                  >
                    {r.title}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <p className="text-slate-400 text-sm">
            <Link
              to={`${localePrefix}/kits`}
              className="text-primary underline underline-offset-2"
            >
              ← {t("kits.backToList")}
            </Link>{" "}
            ·{" "}
            <Link
              to="/models"
              className="text-primary underline underline-offset-2"
            >
              {t("kits.theModels")}
            </Link>{" "}
            ·{" "}
            <Link
              to={`${localePrefix}/pricing`}
              className="text-primary underline underline-offset-2"
            >
              {t("kits.pricing")}
            </Link>
          </p>
        </div>
      </article>
    </PageLayout>
  );
};

export default KitPage;
